import type { Memory } from '@ralph/contracts'
import { apiGet, apiSend } from './client.ts'

/**
 * Personal memory, as the Memory page needs it (PRD-18 AGM-090…093).
 *
 * Split out of `hooks/use-api.ts` rather than grown inside it because memory stopped being
 * one flat list in phase 4: every row now belongs either to the platform (the preferences
 * that apply everywhere) or to exactly one agent, and a suggested row now has a confirm
 * action that is NOT the enable toggle. Both distinctions are the user's, not the machine's,
 * so they are modelled here rather than derived in JSX.
 *
 * // WIRE: D-api extends `GET /api/memories` rows with `agentId` + `agentDisplayName`
 * // (null on a global preference) and adds `POST /api/memories/:id/confirm` → 200.
 * // `MemorySchema` in packages/contracts/src/api.ts does not carry the two fields yet — the
 * // coordinator owns that file. Until it does, they are declared optional here so a
 * // deployment that has not shipped the API half renders every row as global rather than
 * // rendering `undefined` as an agent name.
 */

export type MemoryRow = Memory & {
  /** The agent this preference belongs to; null/absent means it applies everywhere. */
  agentId?: string | null
  /** Carried by the API so a retired agent's memories stay labeled after the listing is gone (AGM-093). */
  agentDisplayName?: string | null
}

type Page<T> = { items: T[]; nextCursor: string | null }

export const fetchMemories = (): Promise<Page<MemoryRow>> => apiGet<Page<MemoryRow>>('/api/memories')

export const createMemory = (input: { category: Memory['category']; statement: string }): Promise<{ id: string }> =>
  apiSend<{ id: string }>('/api/memories', 'POST', input)

export const updateMemory = (
  id: string,
  patch: { enabled?: boolean; statement?: string }
): Promise<{ updated: boolean }> =>
  apiSend<{ updated: boolean }>(`/api/memories/${encodeURIComponent(id)}`, 'PATCH', patch)

export const deleteMemory = (id: string): Promise<{ deleted: boolean }> =>
  apiSend<{ deleted: boolean }>(`/api/memories/${encodeURIComponent(id)}`, 'DELETE')

/**
 * Confirms a suggested memory (AGM-091).
 *
 * Deliberately not `updateMemory({ enabled: true })`. A proposed row is inert until the
 * person it describes accepts the statement — enabling is a switch on something they already
 * agreed to, confirming is the agreement itself. Folding the two into one call would make
 * "turn this back on later" indistinguishable from "yes, that is true about me".
 */
export const confirmMemory = (id: string): Promise<unknown> =>
  apiSend<unknown>(`/api/memories/${encodeURIComponent(id)}/confirm`, 'POST')
